import { useState } from "react";

import { ControlWithFieldsView } from "api/generated/model";
import { useCalendarListQuery } from "components/calendarSelectInput/queries/useCalendarListQuery";
import { useControlProgramsSuspenseQuery } from "org/control/queries/useControlProgramsQuery";
import { useNewControlSuspenseQuery } from "org/control/view/queries/useNewControlQuery";
import { useTagsSuspenseQuery } from "org/tags/queries/useTagsQuery";
import { loadExcelFile } from "utils/spreadsheet/loader";

import { ValidationContext } from "../process/controlValidator";
import { processSpreadsheet } from "../process/process";
import { ValidControlRow } from "../process/types";

export type ProcessorStatus = "idle" | "processing" | "success" | "error";

export type ProcessFileResult =
    | {
          success: true;
          controlGraphs: ControlWithFieldsView[];
          rawData: ValidControlRow[];
      }
    | { success: false; errors: string[] };

interface SpreadsheetProcessorState {
    status: ProcessorStatus;
    result: ProcessFileResult | null;
    errors: string[];
}

interface SpreadsheetProcessor {
    status: ProcessorStatus;
    processedData: ProcessFileResult | null;
    validationErrors: string[];
    processFile: (file: File) => Promise<ProcessFileResult>;
    resetData: () => void;
}

/**
 * Hook to process and validate bulk control import spreadsheets
 *
 * Processing Pipeline:
 * 1. Load Excel file and convert to JSON
 * 2. Parse and validate spreadsheet structure and data types
 * 3. Extract and validate business data
 * 4. Validate against existing data (control programs, tags, calendars)
 * 5. Convert valid data into control graphs
 */
export function useSpreadsheetProcessor(): SpreadsheetProcessor {
    const { data: tagsData } = useTagsSuspenseQuery();
    const { data: controlProgramOverviews } = useControlProgramsSuspenseQuery();
    const { data: calendars } = useCalendarListQuery([]);
    const { data: newControl } = useNewControlSuspenseQuery();

    const controlProgramsData = controlProgramOverviews.content;

    const [state, setState] = useState<SpreadsheetProcessorState>({
        status: "idle",
        result: null,
        errors: [],
    });

    const getValidationContext = (): ValidationContext => ({
        controlPrograms: controlProgramsData.map(
            (program) => program.resource.controlProgram
        ),
        tagCategories: tagsData.tagCategories,
        tags: tagsData.tags,
        calendars: calendars ?? [],
    });

    const processFile = async (file: File): Promise<ProcessFileResult> => {
        setState({ status: "processing", result: null, errors: [] });

        try {
            const loadResult = await loadExcelFile(file);
            if (!loadResult.success) {
                const errorResult = {
                    success: false as const,
                    errors: loadResult.errors,
                };
                setState({
                    status: "error",
                    result: errorResult,
                    errors: loadResult.errors,
                });
                return errorResult;
            }

            const processResult = processSpreadsheet(
                loadResult.data,
                getValidationContext(),
                {
                    segregationOfDutiesEnabled:
                        newControl.content.resource.assignmentSettings
                            .segregationOfDutiesEnabled,
                }
            );
            if (!processResult.success) {
                const errorResult = {
                    success: false as const,
                    errors: processResult.errors,
                };
                setState({
                    status: "error",
                    result: errorResult,
                    errors: processResult.errors,
                });
                return errorResult;
            }

            const successResult = {
                success: true as const,
                controlGraphs: processResult.controlGraphs,
                rawData: processResult.validRows,
            };

            setState({
                status: "success",
                result: successResult,
                errors: [],
            });

            return successResult;
        } catch (error) {
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : "Unknown error processing file";
            const errorResult = {
                success: false as const,
                errors: [errorMessage],
            };

            setState({
                status: "error",
                result: errorResult,
                errors: [errorMessage],
            });

            return errorResult;
        }
    };

    return {
        status: state.status,
        processedData: state.result,
        validationErrors: state.errors,
        processFile,
        resetData: () =>
            setState({
                status: "idle",
                result: null,
                errors: [],
            }),
    };
}
